import { Component, computed, inject, signal, TemplateRef, ViewChild } from "@angular/core";
import { FormGroup, FormBuilder } from "@angular/forms";
import { Router, RouterModule } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { forkJoin, Observable, of } from "rxjs";
import { CartService } from "src/app/services/api/carts.service";
import { UtilitiesService } from "src/app/services/utilities-service/utilities.service";
import {
  statusClassWithCondition,
  TableColumn,
} from "src/app/services/model/tables.model";
import { SharedModule } from "src/shared/shared.module";
import { DateFormat } from "src/utils/enum/date-format.enum";
import { Clipboard } from "@angular/cdk/clipboard";
import { ToastService } from "src/app/services/toast-service/toast.service";


@Component({
  selector: "app-purchase-order",
  imports: [RouterModule, SharedModule, TranslateModule],
  templateUrl: "./purchase-order.component.html",
  styleUrl: "./purchase-order.component.scss",
})
export class PurchaseOrderComponent {
  @ViewChild('trackingNo', { static: true }) trackingNo: TemplateRef<any>;
  @ViewChild('trackingAction', { static: true }) trackingAction: TemplateRef<any>;



  dataSource: any = {
    offset: 0,
    limit: 10,
    page: 1,
    total_page: 10,
    total: 0,
    data: [],
  };
  dataColumns: TableColumn[] = <TableColumn[]>[];

  #transactionService = inject(CartService);

  isLoading = signal(true);
  tableLoading = signal(true);

  form: FormGroup;
  #router = inject(Router);
  #fb = inject(FormBuilder);
  #utilitiesService = inject(UtilitiesService);
  #clipboard = inject(Clipboard);
  #toastService = inject(ToastService);
  #translate = inject(TranslateService);




  transactionStatus: statusClassWithCondition[];

  /**
   * Everything the server sent, before filtering.
   *
   * The list arrives whole — there is no server paging here, the component
   * slices it itself — so the status tabs and the code search filter in memory.
   */
  allTransactions = signal<any[]>([]);
  /** null = every status. */
  statusFilter = signal<number | null>(null);
  codeSearch = signal("");

  /** The four statuses, each with how many orders are in it. Zero-count
   *  statuses still show, because their absence is information too. */
  statusTabs = computed(() =>
    (this.transactionStatus ?? []).map((status) => ({
      id: status.id,
      name: (status as any).name,
      count: this.allTransactions().filter(
        (row) => row.transaction_status_id === status.id
      ).length,
    }))
  );

  filteredTransactions = computed(() => {
    const status = this.statusFilter();
    const term = this.codeSearch().trim().toLowerCase();
    return this.allTransactions().filter((row) => {
      if (status !== null && row.transaction_status_id !== status) return false;
      if (term && !`${row.code ?? ""}`.toLowerCase().includes(term)) return false;
      return true;
    });
  });

  selectStatus(id: number | null): void {
    this.statusFilter.set(this.statusFilter() === id ? null : id);
    this.applyFilter();
  }

  onCodeSearch(value: string): void {
    this.codeSearch.set(value ?? "");
    this.applyFilter();
  }

  private applyFilter(): void {
    const rows = this.filteredTransactions();
    this.dataSource = {
      ...this.dataSource,
      page: 1,
      offset: 0,
      total: rows.length,
      total_page: Math.max(1, Math.ceil(rows.length / this.dataSource.limit)),
      data: rows,
    };
  }

  constructor() { }

  ngOnInit(): void {
    this.buildForm();
    this.fetchData();
    this.initColumns();
  }

  /**
   * The tracking pair is only built when something is actually being shipped.
   *
   * Every course here is a download, so both columns were permanent furniture:
   * a header reading "หมายเลข Tracking" over a column of em dashes, and beside
   * it two buttons that could only ever copy nothing and open nothing.
   */
  initColumns(rows: any[] = []) {
    const hasTracking = rows.some((row) => !!row?.tracking_no);

    this.dataColumns = [
      {
        name: "หมายเลขใบสั่งซื้อ",
        prop: "code",
        propClass: " text-hover-info",
        headerClass: "min-w-125px",
        clickable: true
      },
      {
        name: "วันที่สั่งซื้อ",
        prop: "created_at",
        headerClass: "min-w-150px",
        propClass: "fw-semibold",
        clickable: true

      },
      {
        name: "สถานะการชำระเงิน",
        prop: "transaction_status_name",
        propClass: "fw-semibold badge fs-7 px-3 py-2",
        cellClass: "justify-content-start no-click",
        headerClass: "text-start min-w-125px",
        statusCell: true,
        statusClassCondition: this.transactionStatus || [],
        clickable: true

      },
      {
        name: "ช่องทางการชำระ",
        prop: "payment_type_name",
        headerClass: "min-w-150px",
        propClass: "fw-semibold",
        clickable: true
      },
      {
        name: "จำนวนเงิน",
        prop: "net_price",
        currency: true,
        headerClass: "min-w-150px",
        propClass: "fw-semibold",
        clickable: true
      },
      ...(hasTracking
        ? [
            {
              name: "หมายเลข Tracking",
              prop: "tracking_no",
              headerClass: "min-w-125px",
              propClass: "fw-semibold",
              cellTemplate: this.trackingNo,
              clickable: false,
            },
            {
              name: "",
              prop: "tracking_action",
              headerClass: "",
              propClass: "fw-semibold",
              cellTemplate: this.trackingAction,
              clickable: false,
            },
          ]
        : []),

      // {
      //   name: "ตรวจสอบการชำระ",
      //   prop: "verify_status",
      //   propClass: "fw-semibold",
      // },
    ];
  }

  buildForm() {
    this.form = this.#fb.group({
      datatable: true,
      page: 1,
      order: "best_seller desc",
      limit: 5,
      keywords: null,
      education_id: null,
      semester_id: null,
      course_category_id: null,
      course_category_ids: null,
    });
  }

  _setLoading(value: boolean, time: number = 500) {
    setTimeout(() => {
      this.isLoading.update((_) => value);
    }, time);
  }

  async fetchData() {
    this._setLoading(true, 0);
    forkJoin([
      this.#transactionService.getTransactions() as Observable<any>,
      this.#transactionService.getTransactionStatuses() as Observable<any>,
    ]).subscribe({
      next: ([transactions, transaction_statuses]) => {
        transactions.map((item: any) => {
          item.created_at = this.#utilitiesService.dateFormat(
            item.created_at,
            DateFormat.DATE_TIME
          );

          item.verify_status = item.is_verified
            ? "ตรวจสอบแล้ว"
            : "ยังไม่ตรวจสอบ";
        });
        this.transactionStatus = transaction_statuses;

        this.transactionStatus.map((item: statusClassWithCondition) => {
          switch (item.id) {
            case 1:
              item.class = "badge-light-info";
              break;
            case 2:
              item.class = "badge-light-warning";
              break;
            case 3:
              item.class = "badge-light-success";
              break;
            case 4:
              item.class = "badge-light-danger";
              break;
          }
        });
        this.initColumns(transactions);

        // this.dataSource.data = transactions;
        // this.dataSource = transactions;
        this.allTransactions.set(transactions);
        this.dataSource = {
          offset: 0,
          limit: 10,
          page: 1,
          total_page: Math.ceil(transactions.length / this.dataSource.limit),
          total: transactions.length,
          data: transactions,
        };
        this.applyFilter();

        // this.course_review = course_review;
      },
      error: (error) => { },
      complete: () => {
        this._setLoading(false);
        this.tableLoading.update((_) => false);
      },
    });
  }

  onTableChange(event: any) { }
  onRowClick(row: any) {
    this.#router.navigate([`purchase-order/${row.id}`]);
  }

  copyTrackingNo(row: any) {
    this.#clipboard.copy('EV12132133');
    this.#toastService.toastInfo(this.#translate.instant("APP.COPY_TRACKING_NO"), 3000);

  }


  tracking(row: any) {
    console.log('tracking');
    const trackingUrl = 'https://track.thailandpost.com/' + (row.tracking_no ? '?trackNumber=' + row.tracking_no : '')
    window.open(trackingUrl, '_blank')
  }
}
